import { Disclosure, Flex, Label } from "@gravity-ui/uikit";
import { ReactNode, useState } from "react";

import css from "./Section.module.scss";

type LabelTheme = "normal" | "info" | "danger" | "warning" | "success";

export interface SectionBadge {
  text: string;
  theme?: LabelTheme;
}

interface SectionProps {
  title: string;
  badge?: SectionBadge;
  defaultExpanded?: boolean;
  /**
   * Когда true, секция принудительно раскрыта и игнорирует ручное сворачивание.
   * Используется чтобы показать ошибки валидации внутри свёрнутой секции.
   */
  forceExpanded?: boolean;
  children: ReactNode;
}

export const Section = ({
  title,
  badge,
  defaultExpanded = true,
  forceExpanded,
  children,
}: SectionProps) => {
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded);

  // Родители передают реактивный defaultExpanded (`priceRulesCount > 0`,
  // `!!avitoOptions?.templateId`), который меняется после mount'а — синхронизируем
  // в рендере, иначе авто-раскрытие секции при появлении содержимого не сработает.
  const [prevDefaultExpanded, setPrevDefaultExpanded] =
    useState(defaultExpanded);
  if (defaultExpanded !== prevDefaultExpanded) {
    setPrevDefaultExpanded(defaultExpanded);
    setLocalExpanded(defaultExpanded);
  }

  // Когда внешний `forceExpanded` становится true (например, на ошибке валидации),
  // запоминаем это в локальном стейте чтобы секция осталась раскрытой после снятия.
  const [prevForceExpanded, setPrevForceExpanded] = useState(forceExpanded);
  if (forceExpanded !== prevForceExpanded) {
    setPrevForceExpanded(forceExpanded);
    if (forceExpanded) setLocalExpanded(true);
  }

  const isExpanded = forceExpanded || localExpanded;

  return (
    <Disclosure
      className={css.section}
      size="l"
      arrowPosition="end"
      expanded={isExpanded}
      onUpdate={(v) => {
        if (forceExpanded) return; // нельзя свернуть пока есть ошибки
        setLocalExpanded(v);
      }}
      summary={
        <Flex alignItems="center" gap={2} className={css.summary}>
          <span className={css.title}>{title}</span>
          {badge && (
            <Label theme={badge.theme ?? "normal"} size="s">
              {badge.text}
            </Label>
          )}
        </Flex>
      }
    >
      <div className={css.content}>{children}</div>
    </Disclosure>
  );
};
